import React, { useEffect, useState } from "react";
import styled from "styled-components";

// Components
import Text from "new_components/atoms/Text";
import Modal from "new_components/molecules/Modal";
import SubMenuTemplate from "new_components/templates/SubMenuTemplate";

// Hooks
import { usePageTranslations } from "hooks/usePageTranslations";
import { useLanguage } from "hooks/useLanguage";

// Data
import prompts from "./Data/prompts.json";
import promptsTranslations from "./Data/prompts/translations.json";
import typography from "styles/typography";
import colors from "styles/colors";
import Button from "new_components/atoms/Button";

const ContentSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Content = styled.div`
  max-height: 80vh;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const ContentSection = ({ prompt, t, tPrompts, language }) => {
  // Translate prompt title and description if available
  const translatedTitle = prompt?.id === "d66592b7-56da-40dc-9057-e57bbfbdce79"
    ? tPrompts('actionRecommendations')
    : prompt.title;
  const translatedDescription = prompt?.id === "d66592b7-56da-40dc-9057-e57bbfbdce79"
    ? tPrompts('actionRecommendationsDescription')
    : prompt.ques;

  return (
    <ContentSectionContainer>
      {prompt?.id !== "DEFAULT" && (
        <>
          <Text typographyStyle={typography.subhead.semibold}>
            {translatedTitle}
          </Text>
          <Text typographyStyle={typography.subhead.regular}>{t('overview')}</Text>
        </>
      )}
      <Text typographyStyle={typography.body.regular} color={colors.text.gray}>
        {translatedDescription}
      </Text>
    </ContentSectionContainer>
  );
};

const PromptModal = ({ isOpen, onClose, onConfirm }) => {
  const t = usePageTranslations('app/audit/AI/Copilot/PromptModal/translations');
  const { language } = useLanguage();
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedPromptObj, setSelectedPromptObj] = useState(null);

  // Helper function to translate prompt data
  const tPrompts = (key) => {
    return promptsTranslations[key]?.[language] || promptsTranslations[key]?.['en'] || key;
  };

  const transformedPrompts = prompts.map((promptSection) => {
    const translatedSectionTitle = promptSection.id === 0
      ? tPrompts('starterQuestions')
      : promptSection.sectionTitle;

    const listItems = promptSection.prompts.map((prompt) => ({
      id: prompt.id,
      categoryTitle: prompt.id === "d66592b7-56da-40dc-9057-e57bbfbdce79"
        ? tPrompts('actionRecommendations')
        : prompt.title,
      categoryContent: <ContentSection key={prompt.id} prompt={prompt} t={t} tPrompts={tPrompts} language={language} />,
    }));
    return {
      groupTitle: translatedSectionTitle,
      items: listItems,
    };
  });

  const onConfirmClose = () => {
    onConfirm(selectedPrompt, selectedPromptObj);
    onClose();
  };

  const onCloseWithoutConfirm = () => {
    setSelectedPrompt(null);
    setSelectedPromptObj(null);
    onClose();
  };

  useEffect(() => {
    setSelectedPrompt(null);
    setSelectedPromptObj(null);
  }, [isOpen]);

  return (
    <Modal
      modalWidth="800px"
      modalHeight="700px"
      isOpen={isOpen}
      onClose={onCloseWithoutConfirm}
      modalHeading={t('promptLibrary')}
      modalBody={
        <Content>
          <ActionRow>
            <Button
              disabled={!selectedPrompt}
              onClick={() => {
                if (!selectedPrompt) return;
                onConfirmClose();
              }}
            >
              {t('select')}
            </Button>
          </ActionRow>
          <SubMenuTemplate
            menuItems={transformedPrompts}
            selectedItem={selectedPrompt}
            onItemSelect={(p) => {
              setSelectedPrompt(p?.categoryContent?.props?.prompt?.prompt);
              setSelectedPromptObj(p);
            }}
            type="single"
            // searchable
          />
        </Content>
      }
    />
  );
};

export default PromptModal;
